import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { IconButton, InputAdornment } from '@material-ui/core';
import { Clear as ClearIcon } from '@material-ui/icons';
import {
  KeyboardDatePicker,
  KeyboardDatePickerProps
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

const StyledKeyboardDatePicker = styled(KeyboardDatePicker)`
  & > .MuiOutlinedInput-adornedEnd {
    padding-right: 2px;
  }
  & > .MuiOutlinedInput-adornedStart {
    padding-left: 2px;
  }
`;

export interface InputDatePickerProps {
  value: string | Date | null | undefined;
  label?: string;
  disabled?: boolean;
  format?: string;
  placeholder?: string;
  setDateHours?: boolean;
  birthdate?: boolean;
  clearable?: boolean;
  autoCloseOnSelect?: boolean;
  pickerProps?: Partial<KeyboardDatePickerProps>;
  onChange?: (value: Date | null) => void;
}

const InputDatePicker: React.FC<InputDatePickerProps> = props => {
  const {
    label,
    format,
    placeholder,
    birthdate,
    setDateHours,
    disabled,
    clearable,
    autoCloseOnSelect,
    onChange
  } = props;
  const value =
    props.value && !isNaN(new Date(props.value).getTime()) ? props.value : null;

  const handleChange = useCallback(
    (date: MaterialUiPickersDate) => {
      if (!date) {
        onChange?.(date);
      } else if (!isNaN(date.getTime())) {
        if (setDateHours) {
          // create copy of date and set hours
          date = new Date(date);
          date.setHours(23, 59, 59);
        }
        onChange?.(date);
      }
    },
    [setDateHours, onChange]
  );

  const datePickerProps = useMemo(() => {
    const pickerProps: Partial<KeyboardDatePickerProps> = {
      ...props.pickerProps
    };
    if (birthdate) {
      pickerProps.disableFuture = true;
      pickerProps.openTo = 'year';
      pickerProps.views = ['year', 'month', 'date'];
    }
    if (clearable) {
      pickerProps.InputProps = {
        endAdornment: (
          <InputAdornment position='end'>
            <IconButton disabled={disabled} onClick={() => handleChange(null)}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        )
      };
    }
    return pickerProps;
  }, [birthdate, clearable, disabled, props.pickerProps, handleChange]);

  return (
    <StyledKeyboardDatePicker
      value={value}
      label={label}
      disabled={disabled}
      variant='inline'
      inputVariant='outlined'
      placeholder={placeholder}
      format={format || 'MM/dd/yyyy'}
      autoOk={Boolean(autoCloseOnSelect)}
      InputAdornmentProps={{ position: clearable ? 'start' : 'end' }}
      onChange={handleChange}
      onAccept={handleChange}
      {...datePickerProps}
    />
  );
};

export default InputDatePicker;
